<template>
    <div class="col-xs-8 col-md-8 col-lg-8 pl-0">
        <div>
            <x-number
                v-model="localStylingOptions.nrClasses"
                :text="$t('dashboard.styling.nrClasses')"
                :allowDecimals="true"
                required
            >
            </x-number>
            <x-number
                v-model="localStylingOptions.minValue"
                :text="$t('dashboard.styling.minValue')"
                :allowDecimals="true"
                required
            >
            </x-number>
            <x-number
                v-model="localStylingOptions.maxValue"
                :text="$t('dashboard.styling.maxValue')"
                :allowDecimals="true"
                required
            >
            </x-number>
            <x-number
                v-model="localStylingOptions.roundingUnit"
                :text="$t('dashboard.styling.roundingUnit')"
                required
            >
            </x-number>
            <b-button
                class="mb-3"
                @click="$_generateStyleRules()"
                variant="secondary"
                :disabled="isGenerateButtonDisabled"
            >
                {{ $t('dashboard.styling.generate') }}
                <span class="fas fa-check"></span>
            </b-button>
        </div>
        <!-- Rules -->
        <v-draggable :list="localStylingOptions.rules" handle=".fa-arrows-alt">
            <div
                role="tablist"
                v-for="(rule, index) in localStylingOptions.rules"
                :key="index"
            >
                <b-card v-if="showRule(rule)" no-body class="mb-1">
                    <b-card-header
                        v-b-toggle="`collapseRule${index}`"
                        header-tag="header"
                        class="p-1"
                        role="tab"
                        data-toggle="collapse"
                    >
                        <em class="fa fa-arrows-alt"></em>
                        <span>{{ rule.title }}</span>
                        <span class="float-right">
                            <!-- Delete icon in header -->
                            <a
                                :id="`btnDelete${index}`"
                                :name="`btnDelete${index}`"
                                :title="$t('removeAdvancedStylingRule')"
                                class="pr-4"
                                @click.stop.prevent="$_removeRule(index)"
                            >
                                <em class="fas fa-trash-alt fa-sm"></em>
                            </a>
                            <!-- Expand/collapse icon -->
                            <em class="fa iconCollapseExpand"></em>
                        </span>
                    </b-card-header>
                    <b-collapse
                        :id="`collapseRule${index}`"
                        accordion="rule-accordion"
                        role="tabpanel"
                        class="pl-3"
                    >
                        <!-- Lower bound -->
                        <div class="form-inline style-item pt-3">
                            <div class="form-group">
                                <label
                                    :for="`lowerBound${index}`"
                                    class="custom style-label-md sld-attribute"
                                >
                                    {{ $t('dashboard.styling.lowerBound') }}
                                </label>
                                <x-number
                                    v-if="!isOtherRule(rule)"
                                    :name="`lowerBound${index}`"
                                    :id="`lowerBound${index}`"
                                    v-model="rule.lowerBound"
                                    @input="$_updateTitle(index)"
                                    :allowDecimals="true"
                                    required
                                >
                                </x-number>
                                <span v-else>-</span>
                            </div>
                        </div>
                        <!-- Upper bound -->
                        <div class="form-inline style-item">
                            <div class="form-group">
                                <label
                                    :for="`upperBound${index}`"
                                    class="custom style-label-md sld-attribute"
                                >
                                    {{ $t('dashboard.styling.upperBound') }}
                                </label>
                                <x-number
                                    v-if="!isOtherRule(rule)"
                                    :name="`upperBound${index}`"
                                    :id="`upperBound${index}`"
                                    v-model="rule.upperBound"
                                    @input="$_updateTitle(index)"
                                    :allowDecimals="true"
                                    required
                                >
                                </x-number>
                                <span v-else>-</span>
                            </div>
                        </div>
                        <!-- Line attribute -->
                        <line-attribute
                            v-if="$_showLineStyle(index)"
                            :settings="$_getStyleSettings(index)"
                            :name="`lineStyle${index}`"
                            :geometryTypeArray="geometryTypeArray"
                            :isIconSelected="$_isIconSelected(index)"
                            @update="$_updateSettings($event, index)"
                            :showSvgStyler="true"
                        >
                        </line-attribute>
                        <!-- Icon -->
                        <div
                            v-if="hasPointGeometry"
                            class="form-inline style-item"
                        >
                            <div class="form-group align-items-start">
                                <label class="custom style-label-md">
                                    {{ $t('icon') }}
                                </label>
                                <image-loader
                                    :id="`icon_${index}`"
                                    :name="`icon_${index}`"
                                    :imgExtendedClass="
                                        imgExtendedClass(index, rule)
                                    "
                                    v-model="rule.icon"
                                />
                            </div>
                        </div>
                        <!-- Svg styler for icon -->
                        <div v-if="isSvgIcon(index, rule)" class="form-inline">
                            <label class="style-label-md"></label>
                            <svg-styler
                                :source="rule.icon"
                                :hasPointGeometry="true"
                                :settings="getIconSettings(index)"
                                @update="$_updateIconSettings($event, index)"
                            >
                            </svg-styler>
                        </div>
                    </b-collapse>
                </b-card>
            </div>
        </v-draggable>
        <!-- Add rule -->
        <div>
            <b-link id="addRule" name="addRule" @click="$_addRule">
                <em class="fa fa-plus pr-1"></em
                >{{ $t('dashboard.styling.addItem') }}
            </b-link>
        </div>
    </div>
</template>

<script>
    import { CUSTOM_STYLING_RULE_OTHER } from '../../../constants';
    import { ICON_UNITS } from '../../../constants/api';
    import { isSvgBase64Image } from '../../../services/utilities';
    import modalMixin from '../../Mixins/ModalMixin.vue';
    import draggable from 'vuedraggable';
    import XNumber from '../../../components/BaseComponents/x-number.vue';
    import lineAttribute from './LineAttribute.vue';
    import imageLoader from '../../../components/ImageLoader.vue';
    import SvgStyler from '../../../components/BaseComponents/SvgStyler.vue';
    import { Gradient } from '../../../services/dashboard/gradient';
    import { getIntervals } from '../../../services/dashboard/ranges';

    export default {
        mixins: [modalMixin],
        components: {
            'line-attribute': lineAttribute,
            'image-loader': imageLoader,
            'svg-styler': SvgStyler,
            'x-number': XNumber,
            'v-draggable': draggable
        },
        props: {
            hasPointGeometry: { type: Boolean, required: true },
            hasPointGeometryOnly: { type: Boolean, required: true },
            geometryTypeArray: { type: Array, required: false, default: () => [] },
            customStylingOptions: { type: Object, required: true },
            layerSourceName: {
                type: String,
                required: false,
                default: ''
            }
        },
        data() {
            return {
                localStylingOptions: null,
                mutateStyleItem: false,
                lastNrClasses : 0,
                lastMinValue: 0,
                lastMaxValue: 0,
                lastRoundingUnit: 0
            };
        },
        mounted() {
            this.mutateStyleItem = this.customStylingOptions?.attribute != null;
        },
        computed: {
            $_getTitle() {
                return (rule) => {
                    return this.isOtherRule(rule)
                        ? this.$t('labelOther')
                        : `${rule.lowerBound ?? 0} - ${rule.upperBound ?? 0}`;
                };
            },
            isGenerateButtonDisabled() {
                return !(
                    this.localStylingOptions.nrClasses > 0 &&
                    this.localStylingOptions.roundingUnit > 0 &&
                    this.localStylingOptions.maxValue >
                        this.localStylingOptions.minValue
                );
            },
            isOtherRule() {
                return (rule) => {
                    return rule.filterLiteral === CUSTOM_STYLING_RULE_OTHER;
                };
            },
            showRule() {
                return (rule) => {
                    return this.isOtherRule(rule)
                        ? this.localStylingOptions.useOtherRule
                        : true;
                };
            },
            $_showLineStyle() {
                return (index) => {
                    return (
                        !this.hasPointGeometryOnly || !this.$_isIconSelected(index)
                    );
                };
            },
            $_getStyleSettings() {
                return (index) => {
                    return {
                        fillColor:
                            this.localStylingOptions.rules[`${index}`]?.fillColor ||
                            'rgba(0,0,0,1)',
                        strokeColor:
                            this.localStylingOptions.rules[`${index}`]
                                ?.strokeColor || 'rgba(0,0,0,1)',
                        strokeWidth:
                            this.localStylingOptions.rules[`${index}`]
                                ?.strokeWidth || 3,
                        pointSize:
                            this.localStylingOptions.rules[`${index}`]?.pointSize ||
                            5,
                        nameSvg:
                            this.localStylingOptions.rules[`${index}`]?.nameSvg ||
                            null,
                        fillSvg:
                            this.localStylingOptions.rules[`${index}`]?.fillSvg ||
                            '#ffffff',
                        fillOpacitySvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.fillOpacitySvg || 1,
                        strokeSvg:
                            this.localStylingOptions.rules[`${index}`]?.strokeSvg ||
                            '#000000',
                        strokeWidthSvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.strokeWidthSvg || 1,
                        strokeOpacitySvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.strokeOpacitySvg || 1,
                        sizeSvg:
                            this.localStylingOptions.rules[`${index}`]?.sizeSvg ||
                            15,
                        rotationSvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.rotationSvg || 0
                    };
                };
            },
            getIconSettings() {
                return (index) => {
                    return {
                        fillSvg:
                            this.localStylingOptions.rules[`${index}`]?.iconFill ||
                            '#ffffff',
                        fillOpacitySvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.iconFillOpacity || 1,
                        strokeSvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.iconStroke || '#000000',
                        strokeWidthSvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.iconStrokeWidth || 1,
                        strokeOpacitySvg:
                            this.localStylingOptions.rules[`${index}`]
                                ?.iconStrokeOpacity || 1,
                        sizeSvg:
                            this.localStylingOptions.rules[`${index}`]?.iconSize ||
                            15,
                        sizeUnits:
                            this.localStylingOptions.rules[`${index}`]
                                ?.iconSizeUnits || ICON_UNITS.pixels
                    };
                };
            },
            $_isIconSelected() {
                return (index) => {
                    return !!this.localStylingOptions.rules[`${index}`].icon;
                };
            },
            imgExtendedClass() {
                return (index, rule) => {
                    return this.isSvgIcon(index, rule) ? 'd-none' : 'pb-2';
                };
            },
            isSvgIcon() {
                return (index, rule) => {
                    return (
                        this.$_isIconSelected(index) && isSvgBase64Image(rule.icon)
                    );
                };
            }
        },
        methods: {
            $_updateTitle(index) {
                const rule = this.localStylingOptions.rules[`${index}`];
                rule.title = this.$_getTitle(rule);
            },
            $_removeRule(index) {
                const rule = this.localStylingOptions.rules[index];
                const data = {
                    message: this.$t('deleteConfirmation', [
                        `${this.$_getTitle(rule)}`
                    ])
                };
                this.msgBoxConfirm(data).then((confirmed) => {
                    if (confirmed) {
                        this.localStylingOptions.rules.splice(index, 1);
                    }
                });
            },
            $_generateStyleRules() {
                if (this.$_isScreenValuesChanged()) {
                    this.localStylingOptions.rules = [];
                }
                // wait for DOM to reset color components
                this.$nextTick(() => {
                    let ruleArray = [];
                    let colorArray = this.$_generateGradients(
                        this.localStylingOptions.nrClasses
                    );

                    // if a user deleted some items, then count of
                    if (this.$_isScreenValuesChanged()) {
                        ruleArray = getIntervals(
                            this.localStylingOptions.minValue,
                            this.localStylingOptions.maxValue,
                            colorArray.length,
                            this.localStylingOptions.roundingUnit
                        );
                        ruleArray = ruleArray.map((rule, index) => {
                            return {
                                ...this.$_getStyleSettings(null),
                                ...this.getIconSettings(null),
                                filterPropertyName: this.localStylingOptions.attribute,
                                filterLiteral: '',
                                lowerBound: rule[0],
                                upperBound: rule[1],
                                fillColor: colorArray[index]
                            };
                        });
                        // Add 'other' rule at the end
                        ruleArray.push({
                            ...this.$_getStyleSettings(null),
                            ...this.getIconSettings(null),
                            filterPropertyName: this.localStylingOptions.attribute,
                            filterLiteral: CUSTOM_STYLING_RULE_OTHER,
                            fillColor: 'rgba(255,255,0,1)' // yellow
                        });
                        // set title of rule
                        ruleArray = ruleArray.map((rule) => {
                            return {
                                ...rule,
                                title: this.$_getTitle(rule)
                            };
                        });
                        this.localStylingOptions.rules = ruleArray;
                        this.$_saveScreenValues();
                    } else {
                        // use existing array, dont change 'overig'
                        let otherRule = null;
                        this.localStylingOptions.rules.forEach((stylingOption) => {
                            if (stylingOption.filterLiteral === CUSTOM_STYLING_RULE_OTHER) {
                                // save for add again later
                                otherRule = JSON.parse(JSON.stringify(stylingOption));
                            } else {
                                ruleArray.push(JSON.parse(JSON.stringify(stylingOption)));
                            }
                        });
                        this.localStylingOptions.rules = [];
                        this.$nextTick(() => {
                            ruleArray.map((rule, index) => {
                                rule.fillColor = colorArray[index];
                                return rule;
                            });
                            ruleArray.push(otherRule);
                            this.localStylingOptions.rules = ruleArray;
                            this.$_saveScreenValues();
                        });
                    }
                });
            },
            $_saveScreenValues() {
                this.lastNrClasses = this.localStylingOptions.nrClasses;
                this.lastMinValue = this.localStylingOptions.minValue;
                this.lastMaxValue = this.localStylingOptions.maxValue;
                this.lastRoundingUnit = this.localStylingOptions.roundingUnit;
            },
            $_isScreenValuesChanged() {
                return (this.lastNrClasses !== this.localStylingOptions.nrClasses ||
                        this.lastMinValue !== this.localStylingOptions.minValue ||
                        this.lastMaxValue !== this.localStylingOptions.maxValue ||
                        this.lastRoundingUnit !== this.localStylingOptions.roundingUnit);
            },
            $_calculateScreenValues() {
                let min = Number.MAX_SAFE_INTEGER;
                let max = Number.MIN_SAFE_INTEGER;
                this.localStylingOptions.rules
                    .filter((rule) => rule.filterLiteral !== CUSTOM_STYLING_RULE_OTHER)
                    .forEach(rule => {
                        if (min > rule.lowerBound) {
                            min = rule.lowerBound;
                        }
                        if (max < rule.upperBound) {
                            max = rule.upperBound;
                        }
                    });
                if (min !== Number.MAX_SAFE_INTEGER && max !== Number.MIN_SAFE_INTEGER) {
                    this.localStylingOptions.nrClasses = this.localStylingOptions.rules.length - 1;
                    this.localStylingOptions.minValue = min;
                    this.localStylingOptions.maxValue = max;
                }
                this.$_saveScreenValues();
            },
            $_generateGradients(nrColors) {
                const colorArray = new Gradient()
                    .setColorGradient(
                        this.localStylingOptions.colorStart,
                        this.localStylingOptions.colorEnd
                    )
                    .setNumberOfColors(nrColors)
                    .getColors();
                return colorArray;
            },
            $_addRule() {
                this.localStylingOptions.rules.push({
                    ...this.$_getStyleSettings(null),
                    ...this.getIconSettings(null),
                    filterPropertyName: this.localStylingOptions.attribute,
                    filterLiteral: '',
                    lowerBound: 0,
                    upperBound: 0,
                    title: '0 - 0'
                });
                this.$nextTick(() => {
                    this.$root.$emit(
                        'bv::toggle::collapse',
                        `collapseRule${this.localStylingOptions.rules.length - 1}`
                    );
                });
            },
            async $_getMinMaxValues(tableName, columnName) {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetMinMaxTableAttributeValues',
                    parameters: {
                        TableName: tableName,
                        ColumnName: columnName
                    }
                });
                if (result?.data?.length > 0) {
                    this.localStylingOptions.minValue = parseFloat(result.data[0]);
                    if (result?.data?.length > 1) {
                        this.localStylingOptions.maxValue = parseFloat(result.data[1]);
                    }
                } else {
                    this.$_calculateMinMaxValues();
                }
            },
            $_calculateMinMaxValues() {
                if (this.mutateStyleItem && this.localStylingOptions.rules.length > 1) {
                    this.$_calculateScreenValues();
                } else {
                    this.localStylingOptions.minValue = 0;
                    this.localStylingOptions.maxValue = 0;
                    this.localStylingOptions.nrClasses = 1;
                }
                // only once
                this.mutateStyleItem = false;
            },
            $_updateSettings(event, index) {
                this.localStylingOptions.rules[`${index}`].fillColor =
                    event.fillColor;
                this.localStylingOptions.rules[`${index}`].strokeColor =
                    event.strokeColor;
                this.localStylingOptions.rules[`${index}`].strokeWidth =
                    event.strokeWidth;
                this.localStylingOptions.rules[`${index}`].pointSize =
                    event.pointSize;
                this.localStylingOptions.rules[`${index}`].nameSvg = event.nameSvg;
                this.localStylingOptions.rules[`${index}`].fillSvg = event.fillSvg;

                this.localStylingOptions.rules[`${index}`].fillOpacitySvg =
                    event.fillOpacitySvg;

                this.localStylingOptions.rules[`${index}`].strokeSvg =
                    event.strokeSvg;

                this.localStylingOptions.rules[`${index}`].strokeWidthSvg =
                    event.strokeWidthSvg;

                this.localStylingOptions.rules[`${index}`].strokeOpacitySvg =
                    event.strokeOpacitySvg;
                this.localStylingOptions.rules[`${index}`].sizeSvg = event.sizeSvg;
                this.localStylingOptions.rules[`${index}`].rotationSvg =
                    event.rotationSvg;
            },
            $_updateIconSettings(event, index) {
                this.localStylingOptions.rules[`${index}`].iconFill = event.fillSvg;
                this.localStylingOptions.rules[`${index}`].iconFillOpacity =
                    event.fillOpacitySvg;
                this.localStylingOptions.rules[`${index}`].iconStroke =
                    event.strokeSvg;
                this.localStylingOptions.rules[`${index}`].iconStrokeWidth =
                    event.strokeWidthSvg;
                this.localStylingOptions.rules[`${index}`].iconStrokeOpacity =
                    event.strokeOpacitySvg;
                this.localStylingOptions.rules[`${index}`].iconSize = event.sizeSvg;
                this.localStylingOptions.rules[`${index}`].iconSizeUnits =
                    event.sizeUnits;
            }
        },
        watch: {
            localStylingOptions: {
                handler: function (newVal) {
                    if (JSON.stringify(newVal) === JSON.stringify(this.customStylingOptions)) {
                        return;
                    }
                    this.$emit('update', newVal);
                },
                deep: true
            },
            'localStylingOptions.attribute': {
                handler: function (newVal) {
                    if (newVal) {
                        if (this.layerSourceName) {
                            this.$_getMinMaxValues(this.layerSourceName, newVal);
                        } else {
                            this.$_calculateMinMaxValues();
                        }
                    }
                },
                immediate: true
            },
            customStylingOptions: {
                handler: function (newVal) {
                    this.localStylingOptions = JSON.parse(JSON.stringify(newVal));
                    if (!newVal.nrClasses) {
                        this.localStylingOptions.nrClasses = 1;
                    }
                    if (!newVal.minValue) {
                        this.localStylingOptions.minValue = 0;
                    }
                    if (!newVal.maxValue) {
                        this.localStylingOptions.maxValue = 0;
                    }
                    if (!newVal.roundingUnit) {
                        this.localStylingOptions.roundingUnit = 1;
                    }
                },
                deep: true,
                immediate: true
            }
        }
    };
</script>
